import { useMemo } from 'react';
import { Spinner } from '@highereducation/hen-ui';
import { useOfferingSearch } from '@de/hooks/useOfferingSearch';
import { useDynamicExperienceContext } from '@de/providers';
import { Option } from '@de/types';
import { getStepData } from '@de/utils/getStepData';

const MAX_RESULTS = 4;
const COHORT_FLEXIBILITY_VALUE = 'cohort_paced';

export function OfferingResults() {
  const { steps, flowState, handleGoToStep } = useDynamicExperienceContext();

  const {
    subject,
    skills,
    flexibility,
    level,
    subType,
    flexibilityLabel,
    levelLabel,
  } = useMemo(() => {
    const flexibilityData = getStepData('flexibility', steps, flowState);
    const levelData = getStepData('level', steps, flowState);
    const flexibilityOption = flexibilityData.option as Option | undefined;
    const levelOption = levelData.option as Option | undefined;
    const rawFlexibility = flexibilityData.values[0] || '';
    const isCohort = rawFlexibility === COHORT_FLEXIBILITY_VALUE;

    return {
      subject: getStepData('subject', steps, flowState).values[0] || '',
      skills: getStepData('skills', steps, flowState).values,
      flexibility: isCohort ? 'instructor_paced' : rawFlexibility,
      level: levelData.values[0] || '',
      subType: isCohort ? 'Executive Education' : undefined,
      flexibilityLabel: flexibilityOption?.text.toLowerCase() || '',
      levelLabel: levelOption?.text.toLowerCase() || '',
    };
  }, [steps, flowState]);

  const searchPayload = useMemo(
    () => ({
      subjects: subject ? [subject] : [],
      skills,
      flexibility,
      level,
      subType,
      limit: MAX_RESULTS,
      // TODO: The sorting model should be configured from edudirect-admin
      // ML sorting options - handled by offerings-service
      // sortBy: 'ml-fyp',
      // sortUserContext: {
      //   country: additionalUserContext?.country || '',
      //   region: additionalUserContext?.region || '',
      //   isMobile: additionalUserContext?.isMobile ?? false,
      //   searchPageType: SEARCH_PAGE_TYPE,
      // },
      // sortWebContext: additionalWebContext,
      // sortListId: listId,
      // sortMaxResults: MAX_RESULTS,
    }),
    [subject, skills, flexibility, level, subType],
  );

  const { offerings, loading, relaxedFilters, error } =
    useOfferingSearch(searchPayload);

  const relaxedMessage = useMemo(() => {
    if (relaxedFilters.length === 0) return '';

    const labels = relaxedFilters
      .map((filter) =>
        filter === 'flexibility' ? flexibilityLabel : levelLabel,
      )
      .filter(Boolean);

    if (labels.length === 0) return '';

    const formatted = labels.join(' and ');
    return `We didn't find an exact match. To provide you with the best options available, we've broadened your search outside of ${formatted}.`;
  }, [relaxedFilters, flexibilityLabel, levelLabel]);

  const entryStep = steps.find((step) => step.isEntry);

  const shouldDisplayEditButton = !!(
    entryStep &&
    entryStep.id &&
    handleGoToStep
  );

  const onEditGoal = () => {
    if (handleGoToStep && entryStep && entryStep.id) {
      handleGoToStep(entryStep.id, { resetFlowState: true });
    }
  };

  return (
    <section className='bg-accent-300 p-8 rounded-xl flex flex-col gap-4 w-full shadow-lg'>
      <div className='mx-auto'>
        <h1>Mocked Offerings Results Page For Testing</h1>
      </div>
      <div className='flex flex-col md:flex-row gap-2.5 items-center w-full'>
        {shouldDisplayEditButton && (
          <button
            type='button'
            onClick={onEditGoal}
            className='bg-white px-5 py-2 rounded-full flex items-center justify-center shrink-0 no-underline hover:bg-gray-50 transition-colors'
          >
            <span className='text-primary-700 text-base font-bold leading-[25.2px] whitespace-nowrap'>
              Edit your goal
            </span>
          </button>
        )}
      </div>
      {loading ? (
        <Spinner />
      ) : (
        <>
          <span>
            {relaxedMessage ||
              'Explore the options below to find the best path for you.'}
          </span>
          <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 w-full'>
            {offerings?.slice(0, MAX_RESULTS).map((offering) => (
              <article key={offering.id}>
                <h3>{offering.name}</h3>
              </article>
            ))}
          </div>
        </>
      )}
      {!!error && <p>failed to fetch offerings</p>}
    </section>
  );
}
